import { useId } from 'react'
import { motion } from 'framer-motion'
import type { EvolutionTier } from '@/milestones/types'

interface Props {
  size: number
  tier: EvolutionTier
}

interface RingTone {
  outer: string
  inner: string
  highlight: string
  shadow: string
}

const RING_TONES: Record<EvolutionTier, RingTone> = {
  0: { outer: '#3f424d', inner: '#262a32', highlight: '#5d626f', shadow: '#1a1c22' },
  1: { outer: '#a86f3a', inner: '#5e3a18', highlight: '#deab6f', shadow: '#3a230d' },
  2: { outer: '#c5cdd9', inner: '#7d8896', highlight: '#eef2f8', shadow: '#4f5660' },
  3: { outer: '#f1c14f', inner: '#9a6e1a', highlight: '#fde7a7', shadow: '#5a3f0d' },
  4: { outer: '#5be1b6', inner: '#177b66', highlight: '#b6f5e0', shadow: '#0a3c33' },
  5: { outer: '#7fd0ff', inner: '#1a78c4', highlight: '#d5edff', shadow: '#0a3b6b' },
}

function polygonVertices(cx: number, cy: number, r: number, sides: number) {
  return Array.from({ length: sides }).map((_, i) => {
    const a = (i / sides) * Math.PI * 2 - Math.PI / 2
    return {
      x: cx + Math.cos(a) * r,
      y: cy + Math.sin(a) * r,
      angle: a,
    }
  })
}

function pointsString(verts: ReturnType<typeof polygonVertices>) {
  return verts.map((v) => `${v.x.toFixed(2)},${v.y.toFixed(2)}`).join(' ')
}

export function RankRing({ size, tier }: Props) {
  const id = useId().replace(/:/g, '')
  const cx = size / 2
  const cy = size / 2
  const rOuter = size * 0.48
  const rInner = size * 0.36
  const tone = RING_TONES[tier]

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="absolute inset-0 size-full"
      style={{ overflow: 'visible' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`ring-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tone.highlight} />
          <stop offset="40%" stopColor={tone.outer} />
          <stop offset="100%" stopColor={tone.shadow} />
        </linearGradient>
        <radialGradient id={`well-${id}`} cx="50%" cy="50%">
          <stop offset="60%" stopColor={tone.inner} stopOpacity={tier <= 1 ? 0.95 : 0.85} />
          <stop offset="100%" stopColor={tone.shadow} stopOpacity={1} />
        </radialGradient>
        {}
        <radialGradient id={`platinum-fill-${id}`} cx="50%" cy="50%">
          <stop offset="0%" stopColor={tone.outer} stopOpacity={0.4} />
          <stop offset="100%" stopColor={tone.shadow} stopOpacity={0.15} />
        </radialGradient>
      </defs>

      {}
      <circle cx={cx} cy={cy} r={rInner} fill={`url(#well-${id})`} />

      {}
      <circle
        cx={cx}
        cy={cy}
        r={rInner}
        fill="none"
        stroke={tone.highlight}
        strokeOpacity={tier <= 1 ? 0.3 : 0.55}
        strokeWidth={size * 0.005}
      />

      {tier <= 3 && (
        <ClassicRingBand
          tier={tier as 0 | 1 | 2 | 3}
          cx={cx}
          cy={cy}
          rOuter={rOuter}
          rInner={rInner}
          size={size}
          gradId={`ring-${id}`}
          tone={tone}
        />
      )}

      {tier === 4 && (
        <PlatinumDualHex
          cx={cx}
          cy={cy}
          rInner={rInner}
          rOuter={rOuter}
          size={size}
          gradId={`ring-${id}`}
          fillId={`platinum-fill-${id}`}
          tone={tone}
        />
      )}

      {tier === 5 && (
        <DiamondDualOctagon
          cx={cx}
          cy={cy}
          rInner={rInner}
          rOuter={rOuter}
          size={size}
          gradId={`ring-${id}`}
          tone={tone}
        />
      )}
    </svg>
  )
}

export function getRingTone(tier: EvolutionTier): RingTone {
  return RING_TONES[tier]
}

function ClassicRingBand({
  tier,
  cx,
  cy,
  rOuter,
  rInner,
  size,
  gradId,
  tone,
}: {
  tier: 0 | 1 | 2 | 3
  cx: number
  cy: number
  rOuter: number
  rInner: number
  size: number
  gradId: string
  tone: RingTone
}) {
  const ringBandPath = `M ${cx + rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx - rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx + rOuter} ${cy} M ${cx + rInner + size * 0.025} ${cy} A ${rInner + size * 0.025} ${rInner + size * 0.025} 0 1 0 ${cx - rInner - size * 0.025} ${cy} A ${rInner + size * 0.025} ${rInner + size * 0.025} 0 1 0 ${cx + rInner + size * 0.025} ${cy}`

  return (
    <>
      <path d={ringBandPath} fill={`url(#${gradId})`} fillRule="evenodd" />

      {tier === 0 && <IronNotches cx={cx} cy={cy} rOuter={rOuter} size={size} tone={tone} />}
      {tier === 1 && <BronzeWings cx={cx} cy={cy} rOuter={rOuter} size={size} gradId={gradId} />}
      {tier === 2 && (
        <circle
          cx={cx}
          cy={cy}
          r={rOuter - size * 0.03}
          fill="none"
          stroke={tone.highlight}
          strokeOpacity={0.55}
          strokeWidth={size * 0.005}
        />
      )}
      {tier === 3 && <GoldLaurels cx={cx} cy={cy} rOuter={rOuter} size={size} tone={tone} />}
    </>
  )
}

function IronNotches({
  cx,
  cy,
  rOuter,
  size,
  tone,
}: {
  cx: number
  cy: number
  rOuter: number
  size: number
  tone: RingTone
}) {
  return (
    <>
      {polygonVertices(cx, cy, rOuter, 8).map((f, i) => (
        <rect
          key={`n${i}`}
          x={f.x - size * 0.02}
          y={f.y - size * 0.025}
          width={size * 0.04}
          height={size * 0.05}
          rx={size * 0.005}
          fill={tone.shadow}
          transform={`rotate(${(f.angle * 180) / Math.PI + 90} ${f.x} ${f.y})`}
        />
      ))}
    </>
  )
}

function BronzeWings({
  cx,
  cy,
  rOuter,
  size,
  gradId,
}: {
  cx: number
  cy: number
  rOuter: number
  size: number
  gradId: string
}) {
  return (
    <>
      {[Math.PI, 0].map((a, i) => {
        const x = cx + Math.cos(a) * (rOuter + size * 0.02)
        const y = cy + Math.sin(a) * (rOuter + size * 0.02)
        const tip = size * 0.075
        return (
          <path
            key={`w${i}`}
            d={`M ${x} ${y - tip} L ${x + (i === 1 ? tip : -tip)} ${y} L ${x} ${y + tip} Z`}
            fill={`url(#${gradId})`}
          />
        )
      })}
    </>
  )
}

function GoldLaurels({
  cx,
  cy,
  rOuter,
  size,
  tone,
}: {
  cx: number
  cy: number
  rOuter: number
  size: number
  tone: RingTone
}) {
  return (
    <>
      {[Math.PI, 0].map((a, i) => {
        const baseX = cx + Math.cos(a) * (rOuter - size * 0.005)
        const baseY = cy + Math.sin(a) * (rOuter - size * 0.005)
        const dir = i === 1 ? 1 : -1
        return (
          <g key={`l${i}`} fill={tone.highlight} opacity={0.85}>
            {[-0.06, -0.02, 0.02, 0.06].map((dy, j) => (
              <ellipse
                key={j}
                cx={baseX + dir * size * 0.035}
                cy={baseY + dy * size}
                rx={size * 0.035}
                ry={size * 0.012}
                transform={`rotate(${dir * 25} ${baseX} ${baseY})`}
              />
            ))}
          </g>
        )
      })}
    </>
  )
}

function PlatinumDualHex({
  cx,
  cy,
  rInner,
  rOuter,
  size,
  gradId,
  fillId,
  tone,
}: {
  cx: number
  cy: number
  rInner: number
  rOuter: number
  size: number
  gradId: string
  fillId: string
  tone: RingTone
}) {
  const innerR = rInner + size * 0.045
  const outerR = rOuter + size * 0.04
  const innerVerts = polygonVertices(cx, cy, innerR, 6)
  const outerVerts = polygonVertices(cx, cy, outerR, 6)
  return (
    <>
      {}
      <polygon
        points={pointsString(outerVerts)}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeOpacity={0.7}
        strokeWidth={size * 0.018}
        strokeLinejoin="round"
      />

      {}
      <polygon
        points={pointsString(innerVerts)}
        fill={`url(#${fillId})`}
        stroke={`url(#${gradId})`}
        strokeWidth={size * 0.05}
        strokeLinejoin="round"
      />

      {}
      {innerVerts.map((v, i) => {
        const o = outerVerts[i]
        return (
          <line
            key={`strut${i}`}
            x1={v.x}
            y1={v.y}
            x2={o.x}
            y2={o.y}
            stroke={tone.highlight}
            strokeOpacity={0.25}
            strokeWidth={size * 0.005}
          />
        )
      })}

      {}
      {outerVerts.map((f, i) => (
        <motion.polygon
          key={`gem${i}`}
          points={`${f.x},${f.y - size * 0.025} ${f.x + size * 0.025},${f.y} ${f.x},${f.y + size * 0.025} ${f.x - size * 0.025},${f.y}`}
          fill={tone.highlight}
          animate={{ opacity: [0.55, 1, 0.55], scale: [0.92, 1.08, 0.92] }}
          transition={{ duration: 2.4 + i * 0.18, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: `${f.x}px ${f.y}px` }}
        />
      ))}
    </>
  )
}

function DiamondDualOctagon({
  cx,
  cy,
  rInner,
  rOuter,
  size,
  gradId,
  tone,
}: {
  cx: number
  cy: number
  rInner: number
  rOuter: number
  size: number
  gradId: string
  tone: RingTone
}) {
  const innerR = rInner + size * 0.045
  const outerR = rOuter + size * 0.05
  const innerVerts = polygonVertices(cx, cy, innerR, 8)
  const outerVerts = polygonVertices(cx, cy, outerR, 8)

  return (
    <>
      {}
      <g>
        <animateTransform
          attributeName="transform"
          attributeType="XML"
          type="rotate"
          from={`0 ${cx} ${cy}`}
          to={`360 ${cx} ${cy}`}
          dur="20s"
          repeatCount="indefinite"
        />
        <polygon
          points={pointsString(innerVerts)}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={size * 0.05}
          strokeLinejoin="round"
        />
        {}
        {innerVerts.map((f, i) => (
          <motion.polygon
            key={`igem${i}`}
            points={`${f.x},${f.y - size * 0.02} ${f.x + size * 0.02},${f.y} ${f.x},${f.y + size * 0.02} ${f.x - size * 0.02},${f.y}`}
            fill={tone.highlight}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2 + i * 0.12, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </g>

      {}
      <g>
        <animateTransform
          attributeName="transform"
          attributeType="XML"
          type="rotate"
          from={`360 ${cx} ${cy}`}
          to={`0 ${cx} ${cy}`}
          dur="28s"
          repeatCount="indefinite"
        />
        <polygon
          points={pointsString(outerVerts)}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeOpacity={0.65}
          strokeWidth={size * 0.018}
          strokeLinejoin="round"
        />
        {}
        {outerVerts.map((f, i) => (
          <motion.polygon
            key={`ogem${i}`}
            points={`${f.x},${f.y - size * 0.028} ${f.x + size * 0.028},${f.y} ${f.x},${f.y + size * 0.028} ${f.x - size * 0.028},${f.y}`}
            fill={tone.highlight}
            animate={{ opacity: [0.6, 1, 0.6], scale: [0.9, 1.1, 0.9] }}
            transition={{ duration: 2.2 + i * 0.14, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: `${f.x}px ${f.y}px` }}
          />
        ))}
      </g>
    </>
  )
}
